import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Images, Pencil, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  Badge,
  Button,
  ConfirmModal,
  HoverPopover,
  ToggleGroup,
  type BadgeVariant,
} from "@/components/ui";
import {
  DataTable,
  DataTablePrimaryText,
  buildDataTableLabels,
  queryLoadState,
  type DataTableColumn,
} from "@/components/table";
import {
  ErrorState,
  PageFrame,
  buildListResetToken,
} from "@/components/common";
import { getApiErrorMessage, toastApiError } from "@/lib/errorMessages";
import { regionDisplayName } from "@/lib/regionLabel";
import { resolveCatalogTranslation } from "@/lib/translationResolution";
import {
  carouselSlideKeys,
  carouselSlideService,
  type CarouselSlide,
} from "@/services/carouselSlideService";
import { ACTIVITY_TYPE_ORDER, activityTypeLabel } from "@/constants";
import { Permission } from "@/types";
import { useAuthStore } from "@/store/authStore";
import { useCarouselRegion } from "./useCarouselRegion";

type SortColumn = "order";

const ALL_CATEGORY_COUNT = ACTIVITY_TYPE_ORDER.length;

function formatScheduleDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

function ScheduleCell({
  slide,
  locale,
  alwaysLabel,
  fromLabel,
  untilLabel,
}: {
  slide: CarouselSlide;
  locale: string;
  alwaysLabel: string;
  fromLabel: string;
  untilLabel: string;
}) {
  if (!slide.starts_at && !slide.ends_at) {
    return <span className="text-surface-500">{alwaysLabel}</span>;
  }
  return (
    <div className="space-y-0.5 text-[13px] leading-snug tabular-nums">
      {slide.starts_at ? (
        <p className="text-surface-700 whitespace-nowrap">
          <span className="text-surface-400">{fromLabel}</span>{" "}
          {formatScheduleDate(slide.starts_at, locale)}
        </p>
      ) : null}
      {slide.ends_at ? (
        <p className="text-surface-700 whitespace-nowrap">
          <span className="text-surface-400">{untilLabel}</span>{" "}
          {formatScheduleDate(slide.ends_at, locale)}
        </p>
      ) : null}
    </div>
  );
}

const SLIDE_STATUS_BADGE: Record<CarouselSlide["status"], BadgeVariant> = {
  draft: "warning",
  scheduled: "primary",
  visible: "success",
  disabled: "default",
};

type AdminCarouselPageProps = {
  /** When true, omit page chrome (used as a Devices tab panel). */
  embedded?: boolean;
};

export default function AdminCarouselPage({
  embedded = false,
}: AdminCarouselPageProps) {
  const { t, i18n } = useTranslation("adminCarousel");
  const { t: tCommon } = useTranslation("common");
  const { t: tErrors } = useTranslation("errors");
  const tableLabels = buildDataTableLabels(tCommon);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canWrite = useAuthStore((s) =>
    s.hasPermission(Permission.CAROUSEL_SLIDES_WRITE),
  );

  const [selectedRegionId, setSelectedRegionId] = useState("");
  const [pendingDelete, setPendingDelete] = useState<CarouselSlide | null>(
    null,
  );

  const {
    regions,
    isLoading: regionsLoading,
    error: regionsError,
    retry: retryRegions,
    isRetrying: regionsRetrying,
    regionId,
  } = useCarouselRegion(selectedRegionId);

  const regionOptions = useMemo(
    () =>
      regions.map((region) => ({
        value: region.id,
        label: regionDisplayName(region.code, tCommon, region.name),
      })),
    [regions, tCommon],
  );

  const slidesQueryKey = carouselSlideKeys.list(regionId);

  const {
    data: slides = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: slidesQueryKey,
    queryFn: () => carouselSlideService.list(regionId),
    enabled: Boolean(regionId),
  });

  const deleteMutation = useMutation({
    mutationFn: (slideId: string) => carouselSlideService.delete(slideId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: carouselSlideKeys.all,
      });
      toast.success(t("delete.success"));
      setPendingDelete(null);
    },
    onError: (err) => toastApiError(err),
  });

  const reorderMutation = useMutation({
    mutationFn: ({
      reordered,
    }: {
      reordered: CarouselSlide[];
      previous: CarouselSlide[];
    }) =>
      carouselSlideService.reorder(
        regionId,
        reordered.map((slide) => slide.id),
      ),
    onMutate: async ({ reordered, previous }) => {
      await queryClient.cancelQueries({ queryKey: slidesQueryKey });
      queryClient.setQueryData<CarouselSlide[]>(
        slidesQueryKey,
        reordered.map((slide, index) => ({ ...slide, sort_order: index })),
      );
      return { previous };
    },
    onError: (err, { previous }) => {
      queryClient.setQueryData(slidesQueryKey, previous);
      toastApiError(err);
    },
    onSuccess: () => {
      toast.success(t("toast.reordered"));
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: slidesQueryKey });
    },
  });

  const columns = useMemo<DataTableColumn<CarouselSlide, SortColumn>[]>(
    () => [
      {
        id: "title",
        header: t("columns.slide"),
        sortKey: "order",
        sortKind: "number",
        getSortValue: (row) => row.sort_order,
        defaultWidth: 260,
        cell: (row) => (
          <div className="min-w-0">
            <DataTablePrimaryText>
              {resolveCatalogTranslation(row.title_translations, i18n.language)}
            </DataTablePrimaryText>
            {row.activity?.name ? (
              <p className="text-surface-500 truncate text-[13px]">
                {row.activity.name}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        id: "status",
        header: t("columns.status"),
        defaultWidth: 120,
        cell: (row) => (
          <HoverPopover content={t(`statusHints.${row.status}`)}>
            <Badge
              variant={SLIDE_STATUS_BADGE[row.status]}
              size="sm"
              tabIndex={0}
              className="cursor-help"
              onClick={(event) => event.stopPropagation()}
            >
              {t(`status.${row.status}`)}
            </Badge>
          </HoverPopover>
        ),
      },
      {
        id: "audience",
        header: t("columns.audience"),
        defaultWidth: 200,
        cell: (row) => (
          <div className="min-w-0 text-[13px] leading-snug">
            <p className="text-surface-700 truncate">
              {row.categories.length === ALL_CATEGORY_COUNT
                ? t("categories.all")
                : row.categories
                    .map((cat) => activityTypeLabel(cat))
                    .join(", ")}
            </p>
            <p className="text-surface-500 truncate">
              {row.house_ids.length === 0
                ? t("structures.all")
                : t("structures.count", { count: row.house_ids.length })}
            </p>
          </div>
        ),
      },
      {
        id: "schedule",
        header: t("columns.schedule"),
        defaultWidth: 220,
        cell: (row) => (
          <ScheduleCell
            slide={row}
            locale={i18n.language}
            alwaysLabel={t("schedule.always")}
            fromLabel={t("schedule.from")}
            untilLabel={t("schedule.until")}
          />
        ),
      },
      {
        id: "actions",
        header: "",
        alwaysVisible: true,
        resizable: false,
        defaultWidth: 100,
        cell: (row) => (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              aria-label={t("actions.edit")}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/carousel/${row.id}`);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            {canWrite ? (
              <Button
                variant="ghost"
                size="sm"
                aria-label={t("actions.delete")}
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingDelete(row);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [canWrite, i18n.language, navigate, t],
  );

  const createAction =
    canWrite && regionId ? (
      <Button
        onClick={() =>
          navigate(
            `/admin/carousel/new?region_id=${encodeURIComponent(regionId)}`,
          )
        }
      >
        <Plus className="h-4 w-4" />
        {t("actions.create")}
      </Button>
    ) : null;

  const body = (
    <>
      {regionsLoading ? null : regionsError ? (
        <ErrorState
          title={tErrors("loadRegions")}
          message={getApiErrorMessage(regionsError)}
          onRetry={retryRegions}
          isRetrying={regionsRetrying}
        />
      ) : regions.length === 0 ? (
        <p className="text-surface-600 text-sm">{tCommon("region.empty")}</p>
      ) : (
        <>
          {regionId ? (
            <DataTable
              title={
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="text-surface-500 text-xs font-medium">
                    {tCommon("region.filterLabel")}
                  </span>
                  <ToggleGroup
                    variant="chips"
                    size="sm"
                    value={regionId}
                    onChange={setSelectedRegionId}
                    options={regionOptions}
                    ariaLabel={tCommon("region.filterLabel")}
                  />
                </div>
              }
              actions={embedded ? createAction : undefined}
              labels={tableLabels}
              closeLabel={tCommon("close")}
              loadingLabel={tCommon("loading")}
              rows={slides}
              columns={columns}
              getRowKey={(row) => row.id}
              defaultSortColumn="order"
              preferencesKey="admin-carousel-v4"
              resetToken={buildListResetToken([regionId])}
              loadState={queryLoadState({
                isLoading,
                error,
                errorMessage: t("errors.loadError"),
                retryLabel: tCommon("tryAgain"),
                onRetry: () => void refetch(),
              })}
              emptyMessage={canWrite ? t("emptyWritable") : t("empty")}
              onRowClick={(row) => navigate(`/admin/carousel/${row.id}`)}
              openRowLabel={(row) =>
                resolveCatalogTranslation(row.title_translations, i18n.language)
              }
              reorder={
                canWrite
                  ? {
                      dragHandleLabel: t("actions.dragToReorder"),
                      disabled: reorderMutation.isPending,
                      onReorder: (reordered) => {
                        reorderMutation.mutate({ reordered, previous: slides });
                      },
                    }
                  : undefined
              }
            />
          ) : null}
        </>
      )}

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        isOpen={pendingDelete !== null}
        onClose={() => {
          if (!deleteMutation.isPending) setPendingDelete(null);
        }}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        title={t("delete.title")}
        message={t("delete.body", {
          title: pendingDelete
            ? resolveCatalogTranslation(
                pendingDelete.title_translations,
                i18n.language,
              )
            : "",
        })}
        confirmText={t("delete.confirm")}
        isDestructive
        isLoading={deleteMutation.isPending}
      />
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <PageFrame
      title={t("title")}
      description={t("subtitle")}
      icon={Images}
      actions={createAction ?? undefined}
    >
      {body}
    </PageFrame>
  );
}
